import { useState, useEffect } from 'react'
import { getPromptTemplates, createPromptTemplate, updatePromptTemplate, deletePromptTemplate } from '@/api/client'
import { useApi, useMutation } from '@/hooks/useApi'
import { Plus, Save, Edit2, Trash2, BookOpen, X } from 'lucide-react'

export function PromptSelector({ value, onChange }) {
  const [showDropdown, setShowDropdown] = useState(false)
  const { data: templates } = useApi(() => getPromptTemplates(), [])

  const handleSelect = (template) => {
    onChange(template.prompt)
    setShowDropdown(false)
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setShowDropdown(!showDropdown)}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-left hover:bg-gray-50 transition-colors flex items-center justify-between"
      >
        <span className="text-sm text-gray-700 truncate">
          {value ? 'Custom prompt' : 'Select a template...'}
        </span>
        <BookOpen className="h-4 w-4 text-gray-400" />
      </button>

      {showDropdown && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
          {templates?.map((template) => (
            <button
              key={template.id}
              type="button"
              onClick={() => handleSelect(template)}
              className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0 flex items-start gap-3"
            >
              <BookOpen className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">{template.name}</p>
                {template.description && (
                  <p className="text-sm text-gray-500 truncate">{template.description}</p>
                )}
              </div>
            </button>
          ))}
          {(!templates || templates.length === 0) && (
            <div className="px-4 py-3 text-center text-gray-500 text-sm">No templates available</div>
          )}
        </div>
      )}
    </div>
  )
}

export function PromptLibrary() {
  const [showForm, setShowForm] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState(null)
  const [formData, setFormData] = useState({ name: '', prompt: '', description: '' })

  const { data: templates, refetch: refetchTemplates } = useApi(() => getPromptTemplates(), [])
  const { mutate: doCreate } = useMutation(createPromptTemplate)
  const { mutate: doUpdate } = useMutation(updatePromptTemplate)
  const { mutate: doDelete } = useMutation(deletePromptTemplate)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (editingTemplate) {
      await doUpdate(editingTemplate.id, formData)
    } else {
      await doCreate(formData)
    }
    setShowForm(false)
    setEditingTemplate(null)
    setFormData({ name: '', prompt: '', description: '' })
    refetchTemplates()
  }

  const handleEdit = (template) => {
    setEditingTemplate(template)
    setFormData({ name: template.name, prompt: template.prompt, description: template.description || '' })
    setShowForm(true)
  }

  const handleDelete = async (id) => {
    if (!confirm('Delete this template?')) return
    await doDelete(id)
    refetchTemplates()
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Prompt Library</h1>
        <button
          onClick={() => { setEditingTemplate(null); setFormData({ name: '', prompt: '', description: '' }); setShowForm(true) }}
          className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
        >
          <Plus className="h-4 w-4" />
          New Template
        </button>
      </div>

      {/* Template Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingTemplate ? 'Edit Template' : 'New Template'}
              </h2>
              <button
                onClick={() => { setShowForm(false); setEditingTemplate(null); setFormData({ name: '', prompt: '', description: '' }) }}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prompt</label>
                <textarea
                  value={formData.prompt}
                  onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                  rows={6}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setEditingTemplate(null); setFormData({ name: '', prompt: '', description: '' }) }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                >
                  {editingTemplate ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Templates List */}
      <div className="space-y-4">
        {templates?.map((template) => (
          <div key={template.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex items-start justify-between">
              <div className="flex-1 pr-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`font-medium text-gray-900 ${template.is_default ? 'text-orange-600' : ''}`}>
                    {template.name}
                    {template.is_default && <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">Default</span>}
                  </span>
                </div>
                <p className="text-sm text-gray-500 line-clamp-2">{template.prompt}</p>
                {template.description && (
                  <p className="text-xs text-gray-400 mt-1">{template.description}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {!template.is_default && (
                  <>
                    <button
                      onClick={() => handleEdit(template)}
                      className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                      title="Edit"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(template.id)}
                      className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
        {(!templates || templates.length === 0) && (
          <div className="text-center py-12 text-gray-500">
            <p>No prompt templates yet. Create one to get started.</p>
          </div>
        )}
      </div>
    </div>
  )
}